import { Link } from "react-router-dom";
import { Link2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusBadge, ReviewBadge } from "./ResourceBadges";
import { labelOf } from "./resourceApi";
import { fmtDate } from "@/components/files/fileUtils";

function Row({ r, meta, onSend }) {
  return (
    <tr className="border-b border-zinc-100 align-top hover:bg-zinc-50/60" data-testid={`resource-row-${r.id}`}>
      <td className="py-3 pr-4">
        <Link to={`/resource-library/${r.id}`} className="font-medium text-zinc-900 hover:underline" data-testid={`resource-link-${r.id}`}>
          {r.title}
        </Link>
        <div className="mt-0.5 text-xs text-zinc-500">
          {labelOf(meta.types, r.type)}
          {r.links?.length > 0 && <span className="ml-2 inline-flex items-center gap-0.5"><Link2 className="h-3 w-3" />{r.links.map((l) => l.label).join(", ")}</span>}
        </div>
        <div className="mt-1 flex flex-wrap gap-1">
          {r.categories.map((c) => <span key={c} className="rounded bg-zinc-100 px-1.5 py-0.5 text-2xs text-zinc-600">{labelOf(meta.categories, c)}</span>)}
          {r.tags.slice(0, 4).map((t) => <span key={t} className="rounded border border-zinc-200 px-1.5 py-0.5 text-2xs text-zinc-500">#{t}</span>)}
        </div>
      </td>
      <td className="py-3 pr-4">
        <StatusBadge status={r.status} testId={`resource-status-${r.id}`} />
        {r.pending_version_status && r.status === "approved" && (
          <div className="mt-1 text-2xs text-amber-700">New version {r.pending_version_status === "in_review" ? "in review" : "drafted"}</div>
        )}
      </td>
      <td className="py-3 pr-4 font-mono text-xs text-zinc-700">{r.current_version_number ? `v${r.current_version_number}` : "—"}</td>
      <td className="py-3 pr-4 text-xs text-zinc-700">{r.owner_name || "—"}</td>
      <td className="py-3 pr-4 text-xs text-zinc-700">
        {r.next_review_date ? fmtDate(r.next_review_date) : "—"}
        <div><ReviewBadge state={r.review_state} testId={`resource-review-${r.id}`} /></div>
      </td>
      <td className="py-3 text-right">
        {r.can_send && (
          <Button size="sm" variant="outline" className="h-8" onClick={() => onSend(r)} data-testid={`resource-send-${r.id}`}>
            <Send className="h-3.5 w-3.5 mr-1" /> Send
          </Button>
        )}
      </td>
    </tr>
  );
}

export function ResourceTable({ rows, meta, loading, onSend }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="resource-table">
        <thead>
          <tr className="border-b border-zinc-200 text-left text-xs uppercase tracking-wider text-zinc-500">
            <th className="py-2 pr-4">Resource</th><th className="py-2 pr-4">Status</th><th className="py-2 pr-4">Version</th>
            <th className="py-2 pr-4">Owner</th><th className="py-2 pr-4">Next review</th><th className="py-2" />
          </tr>
        </thead>
        <tbody>
          {loading && <tr><td colSpan={6} className="py-6 text-zinc-500">Loading…</td></tr>}
          {!loading && rows.length === 0 && (
            <tr><td colSpan={6} className="py-6 text-zinc-500" data-testid="resource-table-empty">No resources match these filters.</td></tr>
          )}
          {!loading && rows.map((r) => <Row key={r.id} r={r} meta={meta} onSend={onSend} />)}
        </tbody>
      </table>
    </div>
  );
}
